/** The traction circle: every moment of the lap as lateral against
 *  longitudinal g.
 *
 * Braking sits at the bottom, power at the top, corners out to the sides,
 * and the diagonals are the combined phases — trail-braking in, picking up
 * the throttle while still turning — that separate a quick lap from a tidy
 * one. How much of the ring is used is the whole story, and the four figures
 * underneath are its corners.
 *
 * The axes are calibrated, not assumed. GT7 documents neither the unit nor
 * the sign of its accelerometer, so the comparison fits them against physics
 * the lap already recorded (`accel_calibration` in the port) and hands over
 * a multiplier to g. A fit the logger would not trust draws nothing here
 * either: a confident-looking circle on an unproven scale is worse than none.
 */

import { Card, Kicker } from '@/components/ui';
import { DASH } from '@/lib/format';
import { ggLimit } from '@/components/sessions/compare';
import type { GGExtremes, GGPoint } from '@/components/sessions/compare';

export interface GGCardProps {
  lapNumber: number | null;
  points: GGPoint[] | null;
  extremes: GGExtremes | undefined;
}

const SIZE = 200;
const ZONE_COLOUR = ['var(--color-warn)', 'var(--color-neutral-500)', 'var(--color-ok)'] as const;

export function GGCard({ lapNumber, points, extremes }: GGCardProps) {
  if (!points) return null;
  const limit = ggLimit(points);
  const scale = SIZE / 2 / limit;
  const centre = SIZE / 2;
  const rings: number[] = [];
  for (let g = 0.5; g <= limit; g += 0.5) rings.push(g);

  return (
    <Card elevation="none" label="Traction circle" style={{ padding: 12, gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>traction circle · lap {lapNumber ?? DASH}</Kicker>
        <span style={{ fontSize: 11, color: 'var(--color-neutral-500)' }}>
          rings every 0.5 g · brake, coast, throttle
        </span>
      </div>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`Lateral against longitudinal acceleration for lap ${lapNumber ?? ''}`}
        style={{ width: '100%', maxWidth: 260, aspectRatio: '1 / 1', display: 'block', margin: '0 auto' }}
      >
        {rings.map((g) => (
          <circle
            key={g}
            cx={centre}
            cy={centre}
            r={g * scale}
            fill="none"
            stroke="var(--color-divider)"
            strokeDasharray={Number.isInteger(g) ? undefined : '2 3'}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <line x1={0} y1={centre} x2={SIZE} y2={centre} stroke="var(--color-divider)" vectorEffect="non-scaling-stroke" />
        <line x1={centre} y1={0} x2={centre} y2={SIZE} stroke="var(--color-divider)" vectorEffect="non-scaling-stroke" />
        {points.map((point, index) => (
          <circle
            key={index}
            cx={centre + point.lat * scale}
            cy={centre - point.long * scale}
            r={1.1}
            fill={ZONE_COLOUR[point.zone]}
            fillOpacity={0.55}
          />
        ))}
        <text x={SIZE - 4} y={centre - 4} textAnchor="end" fontSize={8} fill="var(--color-neutral-500)">
          {limit} g
        </text>
        <text x={centre + 4} y={9} fontSize={8} fill="var(--color-neutral-500)">
          power
        </text>
        <text x={centre + 4} y={SIZE - 3} fontSize={8} fill="var(--color-neutral-500)">
          braking
        </text>
      </svg>
      <dl
        className="tabular-nums"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '4px 8px',
          margin: 0,
          fontSize: 12,
          textAlign: 'center',
        }}
      >
        {(
          [
            ['left', extremes?.lat_left],
            ['right', extremes?.lat_right],
            ['braking', extremes?.braking],
            ['power', extremes?.accel],
          ] as const
        ).map(([label, g]) => (
          <div key={label}>
            <dt style={{ fontSize: 10.5, color: 'var(--color-neutral-600)' }}>{label}</dt>
            <dd style={{ margin: 0, color: 'var(--color-text)' }}>
              {typeof g === 'number' ? `${g.toFixed(2)} g` : DASH}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

export default GGCard;
